/**
 * The window's URL names the World.
 *
 * `/<worldId>` opens a World, `/<worldId>/<projectId>` a project in it. Before
 * this the path named only the project and the World came from this browser's
 * pointer, so two browsers could show one project while reading two Worlds and
 * never see each other's devices.
 *
 * A World in the URL BECOMES the pointer, rather than living beside it. Some
 * thirty readers call `readWorldPointer`, and one source of truth means none of
 * them has to learn about the URL. No World in the URL leaves the pointer alone.
 *
 * `/w` and `/w/<projectId>` are the old shape. `w` is never a document id, so
 * the two cannot collide; the router sends both to the new shape.
 */

import { isValidDocumentId } from '@automerge/automerge-repo/slim'
import { PROJECT_KEY } from './sync-server'
import { readWorldPointer, sharingEnabled, writeWorldPointer } from './world'

/** The first segment of the old `/w/<projectId>` links. */
export const LEGACY_SEGMENT = 'w'

const bare = (id: string) => id.replace(/^automerge:/, '')

/** Whether a path segment can name a World or a project: a document id. */
export function isDocumentSegment(segment: string | undefined): boolean {
  return !!segment && isValidDocumentId(bare(segment))
}

/** The path for a World, or for a project in it. */
export function worldPath(worldId: string, projectId?: string | null): string {
  const world = `/${encodeURIComponent(bare(worldId))}`
  return projectId ? `${world}/${encodeURIComponent(bare(projectId))}` : world
}

/** The World a path names, or null: the old shape and other pages name none. */
export function worldFromPath(pathname: string): string | null {
  const first = pathname.split('/')[1]
  if (!first) return null
  let segment: string
  try {
    segment = decodeURIComponent(first)
  } catch {
    return null
  }
  return isDocumentSegment(segment) ? bare(segment) : null
}

/** localStorage key: the World this browser pointed at before a link moved it. */
export const PREVIOUS_WORLD_KEY = 'mod_newsync_world_previous'

/**
 * Point this browser at the World the path names. True when that moved the
 * pointer.
 *
 * Moving it forgets the last-opened project, which belongs to the World being
 * left: `/<worldId>` would otherwise reopen it, and register it, in a World
 * that never had it. The World being left is kept, so a link to a World this
 * browser cannot open does not strand the one it had.
 */
export function adoptWorldFromPath(pathname: string): boolean {
  // A sharing account opens its own World whatever the path says; a link to
  // someone else's World is reached through an invite, not the pointer.
  if (sharingEnabled()) return false
  const worldId = worldFromPath(pathname)
  const before = readWorldPointer()
  if (!worldId || worldId === before) return false
  writeWorldPointer(worldId)
  // A browser refusing storage keeps no pointer; nothing moved.
  if (readWorldPointer() !== worldId) return false
  try {
    if (before) localStorage.setItem(PREVIOUS_WORLD_KEY, before)
    localStorage.removeItem(PROJECT_KEY)
  } catch {
    // best-effort; the World's own list is the fallback
  }
  return true
}

/** The World this browser pointed at before a link moved it, or null. */
export function readPreviousWorldPointer(): string | null {
  try {
    return localStorage.getItem(PREVIOUS_WORLD_KEY) || null
  } catch {
    return null
  }
}
